import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { send, notify } from '../../../services/NotificationService';
import { deleteToolAsset, deleteToolQrCode, getToolAssetQrCode, getToolAssets, getToolQrCodes, updateToolQrCode, upsertToolAsset } from '../../../data/loca/tools';
import ToolPhotoAssets from './photo-assets';

const DEFAULT_QR = {
    size: 420,
    fg: '#00043c',
    bg: '#ffffff'
};
const MAX_NOTICE_FILE_SIZE_BYTES = 50 * 1024 * 1024;

export default function ToolAssetsManager({ tool, settings }) {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [qr, setQr] = useState(DEFAULT_QR);
    const [qrCodes, setQrCodes] = useState({});
    const [toolQrCodes, setToolQrCodes] = useState([]);
    const [qrLoading, setQrLoading] = useState({});
    const [editingQrCodeId, setEditingQrCodeId] = useState(null);
    const [qrCodeForm, setQrCodeForm] = useState(null);
    const [qrSaving, setQrSaving] = useState(false);
    const [deletingQrCodeId, setDeletingQrCodeId] = useState(null);

    const [noticeTitle, setNoticeTitle] = useState('Notice');
    const [noticeDescription, setNoticeDescription] = useState('');
    const [noticeUploading, setNoticeUploading] = useState(false);

    const [videoTitle, setVideoTitle] = useState('Vidéo');
    const [videoDescription, setVideoDescription] = useState('');
    const [youtubeUrl, setYoutubeUrl] = useState('');

    const materialUrlBase = useMemo(
        () => `${settings.publicSiteUrl}/materiel/${tool.customUrl}`,
        [settings.publicSiteUrl, tool.customUrl]
    );

    const mapAssetQrCodes = (list) => {
        const next = {};
        for (const item of list || []) {
            const assetType = (item.supportType || '').toLowerCase();
            if ((assetType === 'notice' || assetType === 'video') && item.qrCodeValue) {
                next[assetType] = {
                    code: item.qrCodeValue,
                    supportType: item.supportType,
                    label: item.label,
                    targetUrl: item.targetUrl
                };
            }
        }
        setQrCodes(next);
    };

    const loadToolQrCodes = async () => {
        const list = await getToolQrCodes(tool.publicId);
        setToolQrCodes(list || []);
        mapAssetQrCodes(list || []);
    };

    const loadAssets = async () => {
        setLoading(true);
        try {
            const list = await getToolAssets(tool.publicId);
            const data = list || [];
            setAssets(data);

            const savedNotice = data.find((asset) => asset.assetType === 'notice');
            setNoticeTitle(savedNotice?.title || 'Notice');
            setNoticeDescription(savedNotice?.description || '');

            const savedVideo = data.find((asset) => asset.assetType === 'video');
            setVideoTitle(savedVideo?.title || 'Vidéo');
            setVideoDescription(savedVideo?.description || '');
            setYoutubeUrl(savedVideo?.youtubeUrl || '');

            await loadToolQrCodes();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAssets();
    }, [tool.publicId]);

    const getByType = (type) => assets.find((a) => a.assetType === type);

    const ensureAssetQrCode = async (assetType) => {
        const qrDto = await getToolAssetQrCode(tool.publicId, assetType, settings.publicSiteUrl);
        if (qrDto?.code) {
            setQrCodes((prev) => ({ ...prev, [assetType]: qrDto }));
            await loadToolQrCodes();
        }
        return qrDto;
    };

    const uploadNotice = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_NOTICE_FILE_SIZE_BYTES) {
            send('warning', 'La notice est trop volumineuse. Taille maximum : 50 Mo.');
            notify();
            event.target.value = '';
            return;
        }

        setNoticeUploading(true);
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                const dto = {
                    assetType: 'notice',
                    title: noticeTitle || file.name,
                    description: noticeDescription,
                    fileName: file.name,
                    fileData: reader.result,
                    youtubeUrl: ''
                };
                const response = await upsertToolAsset(tool.publicId, dto);
                if (response?.id) {
                    await ensureAssetQrCode('notice');
                    send('success', 'Notice enregistrée et QR code associé');
                    notify();
                    await loadAssets();
                }
            } catch (error) {
                send('warning', error.message || "Impossible d'enregistrer la notice");
                notify();
            } finally {
                setNoticeUploading(false);
                event.target.value = '';
            }
        };
        reader.onerror = () => {
            setNoticeUploading(false);
            send('warning', 'Impossible de lire le fichier de notice');
            notify();
        };
        reader.readAsDataURL(file);
    };
    const saveVideo = async () => {
        if (!youtubeUrl) {
            send('warning', 'Veuillez renseigner une URL YouTube');
            notify();
            return;
        }

        const response = await upsertToolAsset(tool.publicId, {
            assetType: 'video',
            title: videoTitle || 'Vidéo',
            description: videoDescription,
            fileName: '',
            fileData: '',
            youtubeUrl
        });

        if (response?.id) {
            await ensureAssetQrCode('video');
            send('success', 'Vidéo enregistrée et QR code associé');
            notify();
            await loadAssets();
        }
    };

    const removeType = async (assetType) => {
        const response = await deleteToolAsset(tool.publicId, assetType);
        if (response?.status === 200) {
            send('success', assetType === 'video' ? 'Vidéo supprimée' : 'Notice supprimée');
            notify();
            loadAssets();
        }
    };

    const qrUrl = (assetType) => {
        const qrCode = qrCodes[assetType];
        if (!qrCode?.code) return '';
        return qrImageUrl(qrCode.code);
    };

    const qrImageUrl = (code) => {
        if (!code) return '';
        const target = encodeURIComponent(`${settings.publicSiteUrl.replace(/\/+$/, "")}/q/${code}`);
        return `https://api.qrserver.com/v1/create-qr-code/?size=${qr.size}x${qr.size}&data=${target}&color=${qr.fg.replace('#', '')}&bgcolor=${qr.bg.replace('#', '')}`;
    };

    const exportQr = (assetType) => {
        exportQrByCode(qrCodes[assetType]?.code, assetType);
    };

    const exportQrByCode = (code, suffix) => {
        if (!code) return;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = qrImageUrl(code);
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = qr.size;
            canvas.height = qr.size;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);

            const link = document.createElement('a');
            link.download = `qrcode-${tool.customUrl}-${suffix || code}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        };
    };
    const generateQr = async (assetType) => {
        const asset = getByType(assetType);
        if (!asset) {
            send('warning', `Enregistrez d'abord ${assetType === 'notice' ? 'la notice' : 'la vidéo'} avant de générer le QR code`);
            notify();
            return;
        }

        setQrLoading((prev) => ({ ...prev, [assetType]: true }));
        try {
            const qrDto = await ensureAssetQrCode(assetType);
            if (qrDto?.code) {
                send('success', `QR ${assetType} généré`);
            } else {
                send('warning', `Impossible de générer le QR ${assetType}`);
            }
            notify();
        } finally {
            setQrLoading((prev) => ({ ...prev, [assetType]: false }));
        }
    };

    const notice = getByType('notice');
    const video = getByType('video');
    const photos = assets.filter((a) => a.assetType === 'photo');

    const renderAssetQr = (assetType, hasAsset) => {
        const code = qrCodes[assetType]?.code;
        if (!code) {
            return (
                <>
                    <p>QR non disponible.</p>
                    <Button
                        size="small"
                        disabled={!hasAsset || qrLoading[assetType]}
                        onClick={() => generateQr(assetType)}
                    >
                        Générer le QR code
                    </Button>
                </>
            );
        }

        return (
            <>
                <img alt={`QR ${assetType}`} src={qrUrl(assetType)} width="180" height="180" />
                <Button size="small" onClick={() => exportQr(assetType)}>Exporter</Button>
            </>
        );
    };

    const startEditQrCode = (item) => {
        setEditingQrCodeId(item.qrCodeId);
        setQrCodeForm({
            code: item.qrCodeValue || '',
            supportType: item.supportType || '',
            label: item.label || '',
            targetUrl: item.targetUrl || '',
            isActive: item.isActive
        });
    };

    const cancelEditQrCode = () => {
        setEditingQrCodeId(null);
        setQrCodeForm(null);
    };

    const updateQrCodeForm = (field, value) => {
        setQrCodeForm((prev) => ({ ...prev, [field]: value }));
    };

    const saveQrCode = async (item) => {
        if (!qrCodeForm?.code?.trim()) {
            send('warning', 'Le code QR est obligatoire');
            notify();
            return;
        }

        setQrSaving(true);
        try {
            const updated = await updateToolQrCode(tool.publicId, item.qrCodeId, {
                code: qrCodeForm.code,
                supportType: qrCodeForm.supportType,
                label: qrCodeForm.label,
                targetUrl: qrCodeForm.targetUrl,
                isActive: qrCodeForm.isActive
            });

            if (updated?.qrCodeId) {
                send('success', 'QR code modifie');
                cancelEditQrCode();
                notify();
                await loadAssets();
            }
        } catch (error) {
            send('warning', error.message || 'Impossible de modifier le QR code');
            notify();
        } finally {
            setQrSaving(false);
        }
    };

    const removeQrCode = async (item) => {
        if ((item.scanCount || 0) > 0) {
            const typedCode = window.prompt(
                `Ce QR code possede ${item.scanCount} scan${item.scanCount > 1 ? 's' : ''}. Pour confirmer sa suppression, recopiez le code : ${item.qrCodeValue}`
            );
            if (typedCode !== item.qrCodeValue) {
                send('warning', 'Suppression annulee : confirmation incorrecte');
                notify();
                return;
            }
        } else {
            if (!window.confirm(`Supprimer le QR code ${item.qrCodeValue} ?`)) {
                return;
            }
        }

        setDeletingQrCodeId(item.qrCodeId);
        try {
            const response = await deleteToolQrCode(tool.publicId, item.qrCodeId);
            if (response?.status === 200) {
                send('success', 'QR code supprime');
                notify();
                if (editingQrCodeId === item.qrCodeId) {
                    cancelEditQrCode();
                }
                await loadAssets();
            }
        } catch (error) {
            send('warning', error.message || 'Impossible de supprimer le QR code');
            notify();
        } finally {
            setDeletingQrCodeId(null);
        }
    };

    const renderQrCodeList = () => {
        if (!toolQrCodes.length) {
            return <p>Aucun QR code associe a cet outil.</p>;
        }

        return (
            <div className="tool-assets-qr-list">
                {toolQrCodes.map((item) => (
                    <div className="tool-assets-qr-list-item" key={item.qrCodeId}>
                        <img alt={`QR ${item.qrCodeValue}`} src={qrImageUrl(item.qrCodeValue)} width="96" height="96" />
                        {editingQrCodeId === item.qrCodeId ? (
                            <>
                                <div className="tool-assets-qr-edit-form">
                                    <InputText value={qrCodeForm.code} onChange={(event) => updateQrCodeForm('code', event.target.value)} placeholder="Code" />
                                    <InputText value={qrCodeForm.supportType} onChange={(event) => updateQrCodeForm('supportType', event.target.value)} placeholder="Type de support" />
                                    <InputText value={qrCodeForm.label} onChange={(event) => updateQrCodeForm('label', event.target.value)} placeholder="Libelle" />
                                    <InputText value={qrCodeForm.targetUrl} onChange={(event) => updateQrCodeForm('targetUrl', event.target.value)} placeholder="URL cible" />
                                    <label className="tool-assets-qr-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={qrCodeForm.isActive}
                                            onChange={(event) => updateQrCodeForm('isActive', event.target.checked)}
                                        />
                                        QR actif
                                    </label>
                                </div>
                                <div className="tool-assets-qr-list-actions">
                                    <Button size="small" disabled={qrSaving} onClick={() => saveQrCode(item)}>Enregistrer</Button>
                                    <Button size="small" severity="secondary" disabled={qrSaving} onClick={cancelEditQrCode}>Annuler</Button>
                                    <Button size="small" severity="danger" disabled={qrSaving || deletingQrCodeId === item.qrCodeId} onClick={() => removeQrCode(item)}>Supprimer</Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <strong>{item.qrCodeValue}</strong>
                                    <p>{item.supportType || 'Machine'}{item.label ? ` - ${item.label}` : ''}</p>
                                    {item.targetUrl && <a href={item.targetUrl} target="_blank" rel="noreferrer">{item.targetUrl}</a>}
                                    <p>{item.isActive ? 'Actif' : 'Inactif'} - {item.scanCount} scan{item.scanCount > 1 ? 's' : ''}{item.lastScannedAtUtc ? ` - dernier scan ${new Date(item.lastScannedAtUtc).toLocaleString('fr-FR')}` : ''}</p>
                                </div>
                                <div className="tool-assets-qr-list-actions">
                                    <Button size="small" onClick={() => startEditQrCode(item)}>Modifier</Button>
                                    <Button size="small" severity="secondary" onClick={() => exportQrByCode(item.qrCodeValue, item.supportType || item.qrCodeValue)}>Exporter</Button>
                                    <Button size="small" severity="danger" disabled={deletingQrCodeId === item.qrCodeId} onClick={() => removeQrCode(item)}>Supprimer</Button>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="tool-assets-panel tool-details-card-full">
            <h2>Assets outil (notice / vidéo / photos)</h2>
            {loading && <p>Chargement...</p>}

            <div className="tool-assets-qr-controls">
                <label>Taille <InputText value={qr.size} onChange={(e) => setQr((prev) => ({ ...prev, size: Number(e.target.value || 420) }))} /></label>
                <label>Avant <input type="color" value={qr.fg} onChange={(e) => setQr((prev) => ({ ...prev, fg: e.target.value }))} /></label>
                <label>Fond <input type="color" value={qr.bg} onChange={(e) => setQr((prev) => ({ ...prev, bg: e.target.value }))} /></label>
            </div>

            <div className="tool-assets-grid">
                <section className="tool-assets-row">
                    <div className="tool-assets-box">
                        <h3>Notice</h3>
                        <InputText value={noticeTitle} disabled={noticeUploading} onChange={(e) => setNoticeTitle(e.target.value)} placeholder="Titre de la notice" />
                        <InputText value={noticeDescription} disabled={noticeUploading} onChange={(e) => setNoticeDescription(e.target.value)} placeholder="Description" />
                        <input type="file" accept="application/pdf,image/*" disabled={noticeUploading} onChange={uploadNotice} />
                        {noticeUploading && <p>Enregistrement de la notice...</p>}
                        {notice && (
                            <div className="tool-assets-actions">
                                <a href={`${materialUrlBase}/notice`} target="_blank" rel="noreferrer">Voir la page publique notice</a>
                                <Button size="small" severity="danger" onClick={() => removeType('notice')}>Supprimer la notice</Button>
                            </div>
                        )}
                    </div>
                    <div className="tool-assets-qr-card">
                        <p>QR notice</p>
                        {renderAssetQr('notice', !!notice)}
                    </div>
                </section>

                <section className="tool-assets-row">
                    <div className="tool-assets-box">
                        <h3>Vidéo YouTube</h3>
                        <InputText value={videoTitle} onChange={(e) => setVideoTitle(e.target.value)} placeholder="Titre vidéo" />
                        <InputText value={videoDescription} onChange={(e) => setVideoDescription(e.target.value)} placeholder="Description" />
                        <InputText value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." />
                        <Button size="small" onClick={saveVideo}>Enregistrer la vidéo</Button>
                        {video && (
                            <div className="tool-assets-actions">
                                <a href={`${materialUrlBase}/video`} target="_blank" rel="noreferrer">Voir la page publique vidéo</a>
                                <Button size="small" severity="danger" onClick={() => removeType('video')}>Supprimer la vidéo</Button>
                            </div>
                        )}
                    </div>
                    <div className="tool-assets-qr-card">
                        <p>QR vidéo</p>
                        {renderAssetQr('video', !!video)}
                    </div>
                </section>
            </div>

            <ToolPhotoAssets tool={tool} photos={photos} onChanged={loadAssets} />

            <section className="tool-assets-row">
                <div className="tool-assets-box">
                    <h3>QR codes associes a l'outil</h3>
                    {renderQrCodeList()}
                </div>
            </section>
        </div>
    );
}
